import logger from '../log';
import racedFetch from '../common/utils/racedFetch';
import { EMPTY_PROBE } from '../priming/interfaces';
import { ProbeRequest } from '../config/ProbeRequests';
import CompositeError from '../common/utils/CompositeError';

const log = logger.getLogger(__filename);
export const DEFAULT_RETRIES = 5;
export const DEFAULT_TIMEOUT = 2000;

export interface PingData {
  hasProbed: boolean;
  probeUrls: PingResponse[];
}

export interface PingResponse {
  url: string;
  statusCode: number;
  probeResponseBody?: string;
}

export interface SingleProbeArgs {
  request: ProbeRequest;
  retries: number;
  timeout: number;
}

const probeSingleUrl = async (args: SingleProbeArgs): Promise<PingResponse> => {
  const { request, retries, timeout } = args;
  const { url } = request;
  const options: RequestInit = {
    cache: 'no-cache',
    method: request.returnResponse ? 'GET' : 'HEAD',
    mode: 'cors'
  };
  log.info(`probeSingleUrl:${url}`);
  const probe = async () => {
    try {
      const res = await racedFetch({ options, retries, timeout, url });
      log.info(`racedFetch: ${url}`, res);
      if (request.returnResponse) {
        const body = await res.json();
        const probeResponseBody = JSON.stringify(body);
        return { url, statusCode: res.status, probeResponseBody };
      }
      return { url, statusCode: res.status };
    } catch (e) {
      const error =
        (e &&
          (e as CompositeError).lastError &&
          (e as CompositeError).lastError()) ||
        e;
      log.info(`probeSingleUrl: ${url}`, error);
      return { url, statusCode: 599 };
    }
  };
  const ret = await probe();
  log.info(`probeSingleUrl: post wait`, ret);
  return ret;
};

export const getUncheckedUrls = (
  requests: ProbeRequest[],
  probedItems?: PingData
): [ProbeRequest[], Promise<PingResponse>[]] => {
  if (!probedItems || !probedItems.hasProbed) {
    log.info('no probed urls found, calling for urls: ', requests);
    return [requests, []];
  }
  const probed = probedItems.probeUrls.reduce(
    (sum: Record<string, boolean>, val: PingResponse) => {
      sum[val.url] = true;
      return sum;
    },
    {}
  );
  const newUrls = requests.reduce(
    (sum: ProbeRequest[], request: ProbeRequest) => {
      if (!probed[request.url]) {
        sum.push(request);
      }
      return sum;
    },
    []
  );

  return [newUrls, probedItems.probeUrls.map(ping => Promise.resolve(ping))];
};

const gatherProbeUrls = async (
  requests: ProbeRequest[],
  probedItems?: PingData
): Promise<PingResponse[]> => {
  const [newRequests, probed] = getUncheckedUrls(requests, probedItems);
  log.warn('gatherProbeUrls:', {
    newRequests,
    probedItems,
    requests,
    probed
  });
  const promises = [
    ...probed,
    ...newRequests.map(request =>
      probeSingleUrl({
        retries: DEFAULT_RETRIES,
        timeout: DEFAULT_TIMEOUT,
        request
      })
    )
  ];
  const ret = await Promise.all(promises);
  return ret;
};

export const mergeProbeUrls = (urls: PingResponse[]): PingResponse[] => {
  const results: Record<string, PingResponse> = urls.reduce((sum, item) => {
    const other = sum[item.url];
    if (!other || (item.statusCode >= 200 && item.statusCode < 300)) {
      sum[item.url] = item;
    }
    return sum;
  }, {} as Record<string, PingResponse>);
  return Object.values(results);
};

/**
 * Get ping data directly by calling a set of urls
 *
 * @param urls - the list of urls to request OPTIONs on
 * @returns PingData results of the requests. If there is no array, hasProbed
 * will be false
 *
 * Always returns a PingData
 */
export const probeUrlList = async (
  requests: ProbeRequest[] | undefined,
  probed?: PingData
): Promise<PingData> => {
  if (!requests || !(requests instanceof Array) || requests.length == 0) {
    log.debug('No probeRequests', { requests });
    if (probed) {
      return probed;
    }
    return EMPTY_PROBE;
  }
  const probeUrls = await gatherProbeUrls(requests, probed);
  log.info('returning probeUrlList', probeUrls);
  return { hasProbed: true, probeUrls };
};
